import { useLiveQuery } from 'dexie-react-hooks'
import { db } from './db'
import type { FailedSerial, OutboxOp } from './types'

/** Drives the network strip's «… در صف» counter. Live — no polling. */
export function useQueuedCount(): number {
  return (
    useLiveQuery(
      () => db.outbox.where('status').anyOf('queued', 'inflight').count(),
      [],
      0,
    ) ?? 0
  )
}

export function useQueuedOps(palletCode?: string): OutboxOp[] {
  return (
    useLiveQuery(
      async () => {
        const queued = await db.outbox.where('status').anyOf('queued', 'inflight').sortBy('seq')
        return palletCode ? queued.filter((op) => op.palletCode === palletCode) : queued
      },
      [palletCode],
      [],
    ) ?? []
  )
}

/**
 * Rejected serials that still need a decision. Ignored serials stay in the rejected
 * operation for auditability, but are not part of the triage backlog.
 */
export function failedSerialsFromRejectedOps(rejected: OutboxOp[]): FailedSerial[] {
  return rejected.flatMap<FailedSerial>((op) =>
    (op.result?.results ?? [])
      .filter(
        (item) =>
          item.status === 'rejected' && !op.ignoredSerialNumbers?.includes(item.serialNumber),
      )
      .map((item) => ({
        opId: op.id,
        palletCode: op.palletCode,
        serialNumber: item.serialNumber,
        reason: item.reason ?? null,
        currentPalletCode: item.currentPalletCode ?? null,
        kind: op.kind,
        captureMethod: op.captureMethod,
        capturedAt: op.capturedAt,
      })),
  )
}

/**
 * Rejected serials, flattened out of their results into per-item rows so the queue
 * screen can triage them one at a time (retry / transfer / delete / ignore).
 */
export function useFailedSerials(): FailedSerial[] {
  return (
    useLiveQuery(async () => {
      const rejected = await db.outbox.where('status').equals('rejected').sortBy('seq')

      return failedSerialsFromRejectedOps(rejected)
    }, [], []) ?? []
  )
}

export function useBasketCount(): number {
  return useLiveQuery(() => db.basket.count(), [], 0) ?? 0
}
